/**
 * Entity list — /settings/custom-modules/$key/entities
 *
 * Lists the entities in a module with quick stats (field count,
 * computed field count). Clicking an entity opens its editor.
 * "New entity" button → create form.
 */

import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Database, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/custom-modules/$key/entities/')({
  component: EntitiesListPage,
})

function EntitiesListPage() {
  const { key: moduleKey } = Route.useParams()

  const { data: entities, isLoading } = useQuery(
    orpc.modules.custom.entities.list.queryOptions({ input: { moduleKey } }),
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Entities</h2>
          <p className="text-sm text-muted-foreground">
            Define the data shapes this module stores.
          </p>
        </div>
        <Link
          to="/settings/custom-modules/$key/entities/new"
          params={{ key: moduleKey }}
        >
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New entity
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
      ) : !entities || entities.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Database className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground mb-3">
              No entities yet. Create the first one to start storing records.
            </p>
            <Link
              to="/settings/custom-modules/$key/entities/new"
              params={{ key: moduleKey }}
            >
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                New entity
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {entities.map((entity) => {
            const fieldCount = (entity.fields as unknown[]).length
            const computedCount = (entity.computedFields as unknown[]).length
            return (
              <Link
                key={entity.id}
                to="/settings/custom-modules/$key/entities/$entityKey"
                params={{ key: moduleKey, entityKey: entity.key }}
              >
                <Card className="hover:bg-muted/50 transition-colors cursor-pointer h-full">
                  <CardHeader>
                    <CardTitle className="text-base">
                      {entity.namePlural ?? entity.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {entity.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                        {entity.description}
                      </p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="font-mono">{entity.key}</span>
                      <span>
                        {fieldCount} {fieldCount === 1 ? 'field' : 'fields'}
                      </span>
                      {computedCount > 0 && (
                        <span>
                          {computedCount} computed
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
